import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Lock, Mail, Scale, CheckCircle, AlertCircle } from "lucide-react";
import { API } from "@/App";

export const SolicitorPortalLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkResult, setMagicLinkResult] = useState(null);

  // Check for magic link token on mount
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    setMagicLinkLoading(true);
    try {
      const response = await axios.post(`${API}/solicitor-portal/auth/magic-link`, { token });
      const { access_token, solicitor, case_id, is_new_account } = response.data;

      // Store token and solicitor info
      localStorage.setItem("solicitor_token", access_token);
      localStorage.setItem("solicitor", JSON.stringify(solicitor));

      if (is_new_account) {
        setMagicLinkResult({
          success: true,
          isNewAccount: true,
          tempPassword: solicitor.temp_password,
          solicitor
        });
        toast.success("Welcome! Your account has been created.");
      } else {
        toast.success(`Welcome back, ${solicitor.name || solicitor.email}!`);
        navigate(case_id ? `/solicitor-portal/case/${case_id}` : "/solicitor-portal");
      }
    } catch (error) {
      setMagicLinkResult({
        success: false,
        error: error.response?.data?.detail || "Invalid or expired magic link"
      });
      toast.error(error.response?.data?.detail || "Invalid or expired magic link");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/solicitor-portal/auth/login`, {
        email,
        password
      });

      const { access_token, solicitor } = response.data;

      // Store token and solicitor info
      localStorage.setItem("solicitor_token", access_token);
      localStorage.setItem("solicitor", JSON.stringify(solicitor));

      toast.success(`Welcome back, ${solicitor.name || solicitor.email}!`);
      navigate("/solicitor-portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAfterSetup = () => {
    navigate("/solicitor-portal");
  };

  // Magic link verification loading state
  if (magicLinkLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-white/95 backdrop-blur">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-700">Verifying your link...</p>
            <p className="text-sm text-slate-500 mt-2">Please wait while we authenticate your access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Magic link result - new account created
  if (magicLinkResult?.success && magicLinkResult.isNewAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Account Created</CardTitle>
            <CardDescription className="text-slate-600">
              Your solicitor portal account is ready
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg border">
              <p className="text-sm text-slate-600 mb-2">Your login credentials:</p>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Email:</span>
                  <span className="font-medium">{magicLinkResult.solicitor.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Temporary Password:</span>
                  <span className="font-mono bg-amber-100 px-2 py-1 rounded text-amber-800">
                    {magicLinkResult.tempPassword}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Please save this password securely. You can change it from your profile settings.
              </p>
            </div>

            <Button
              onClick={handleContinueAfterSetup}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="continue-to-portal-btn"
            >
              Continue to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Magic link error
  if (magicLinkResult?.success === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Link Expired</CardTitle>
            <CardDescription className="text-slate-600">
              {magicLinkResult.error}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              If you have an existing account, please login below. Otherwise, contact the estate agent to request a new invitation link.
            </p>

            <Button
              onClick={() => setMagicLinkResult(null)}
              variant="outline"
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Standard login form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-slate-700 bg-white/95 backdrop-blur" data-testid="solicitor-login-card">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">Solicitor Portal</CardTitle>
          <CardDescription className="text-slate-600">
            Goodacres Residential
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@lawfirm.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="solicitor-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  data-testid="solicitor-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
              data-testid="solicitor-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-sm text-center text-slate-500">
              Need access? Contact the estate agent handling your case to request an invitation.
            </p>
          </div>

          {/* Security notice */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 text-center">
              <Lock className="w-3 h-3 inline mr-1" />
              Secure portal for legal professionals
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SolicitorPortalLogin;
